import { useMemo, useState } from 'react';
import {
    Box, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';

import { useSelector } from 'BMapsRedux';
import DialogContentSection from 'BMapsUI/common/DialogContentSection';
import { DialogPrimaryButton } from 'BMapsUI/common/DialogActionButtons';
import Select from 'BMapsUI/common/Select';
import BMapsTooltip from 'BMapsUI/BMapsTooltip';
import { wrapForDisabledTooltip } from 'BMapsSrc/ui/common/helpers';
import {
    POSE_STYLES, applyMdResults, availablePlots, mdCandidateCompounds, meanRmsf, seriesForPlot,
} from 'BMapsSrc/integrations/md';
import { ImportErrors } from '../../ImportPane/import_components';
import { openMdSidePanel } from '../MdSidePanel';
import MdLineChart from '../MdLineChart';
import ProteinLigandRmsdChart from '../ProteinLigandRmsdChart';
import { LigandPropertiesChart, LigandRmsfChart } from '../LigandAnalysisCharts';
import LigandInteractionDiagram from '../LigandInteractionDiagram';
import {
    ContactsHistogramChart, ContactsTimelineChart, LigandTorsionsPanel, SecondaryStructureChart,
} from '../StructureCharts';
import MdReportPanel from '../MdReportPanel';
import { loadFinalPose, showResidues } from '../mdActions';

const RMSD_PLOT_KEYS = ['backbone_rmsd_a', 'ligand_rmsd_a'];

const CONTACT_ROWS = 15;
const RMSF_ROWS = 8;
const CONTACT_SELECT_MIN = 0.5;

function fmt(value, digits=2) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '';
    return Number(value).toFixed(digits);
}

function fmtPm(stat, digits=2) {
    if (!stat || stat.mean === undefined) return '';
    return stat.sd !== undefined ? `${fmt(stat.mean, digits)} ± ${fmt(stat.sd, digits)}` : fmt(stat.mean, digits);
}

function pct(v) {
    return v === undefined || v === null ? '' : `${Math.round(100 * v)}%`;
}

function PoseBadge({ pose }) {
    const style = POSE_STYLES[pose];
    if (!style) return null;
    return (
        <span style={{
            border: `1px solid ${style.color}`, color: style.color, borderRadius: '0.75rem', padding: '0 0.5rem', fontSize: '0.8rem', marginLeft: '0.5rem',
        }}
        >
            {style.label}
        </span>
    );
}

/** A residue key that selects and zooms to the residue when clicked. */
function ResidueLink({ residue, resname, onShow }) {
    return (
        <Link /* eslint-disable-line jsx-a11y/anchor-is-valid */
            component="button"
            onClick={() => onShow([residue])}
            title="Select and zoom to this residue"
            sx={{ color: 'var(--marketing-blue)' }}
        >
            {`${resname || ''} ${residue}`.trim()}
        </Link>
    );
}

/**
 * Results of the MD jobs chosen in the Jobs tab: per compound, the pose
 * verdict and RMSDs per copy, time-series plots, protein-ligand H-bonds and
 * contacts (click a residue to select it), residue flexibility, and the final
 * poses (loadable as compounds). Apply writes the pose verdict, ligand RMSD
 * and top H-bond onto the compounds the job was submitted for.
 */
export default function MdResults({ selected }) {
    const { localJobState, displayedMdJobs, jobs } = useSelector((state) => state.jobs);
    if (!selected) return null;
    const displayed = (displayedMdJobs || [])
        .map((job) => jobs.find((j) => j.jobid === job.jobid) || job);
    if (displayed.length === 0) {
        return (
            <DialogContentSection title="MD Results" disablePaddingTop>
                <span>
                    No MD results to display.
                    <br />
                    Load results from the
                    {' '}
                    <Link /* eslint-disable-line jsx-a11y/anchor-is-valid */
                        component="button"
                        onClick={() => openMdSidePanel('md_jobs_tab')}
                        sx={{ color: 'var(--marketing-blue)' }}
                    >
                        Jobs tab
                    </Link>
                    {' '}
                    to view them here.
                </span>
            </DialogContentSection>
        );
    }
    return (
        <Box sx={{ overflow: 'auto', height: '100%' }}>
            {displayed.map((job) => (
                <JobResults key={job.jobid} job={job} local={localJobState[job.jobid] || {}} />
            ))}
        </Box>
    );
}

function JobResults({ job, local }) {
    const [applying, setApplying] = useState(false);
    const [messages, setMessages] = useState();
    const md = local.results;
    const title = job.requestmeta?.jobName || job.jobid;
    const method = job.requestmeta?.method || job.jobtype;

    if (!md) {
        return (
            <DialogContentSection title={title} titleVariant="subtitle1">
                <span>{`${method}: results not loaded${local.error ? ` (${local.error})` : ''}.`}</span>
            </DialogContentSection>
        );
    }

    const params = md.params || {};
    const prov = md.provenance || {};
    const compounds = md.compounds || [];
    const ok = compounds.filter((c) => c.status === 'succeeded');
    const candidates = mdCandidateCompounds(local.stash ? { stash: local.stash } : job);
    const applied = local.applied || [];

    const remd = params.replica_exchange;
    const statusLine = [
        method,
        remd
            ? `${params.length_ns} ns × ${remd.replicas} replicas (${remd.temperatures?.[0] ?? params.temperature_k}–${remd.max_temperature_k} K)`
            : `${params.length_ns} ns × ${params.copies} independent run${params.copies === 1 ? '' : 's'} at ${params.temperature_k} K`,
        `${ok.length} of ${compounds.length} compound${compounds.length === 1 ? '' : 's'} simulated`,
        prov.ns_per_day ? `${Math.round(prov.ns_per_day)} ns/day` : null,
        prov.gpu || null,
    ].filter(Boolean).join(' · ');

    let applyDisabled = '';
    if (ok.length === 0) applyDisabled = 'No compound was simulated';
    else if (candidates.length === 0) applyDisabled = 'None of the job’s compounds are loaded in the workspace';

    async function apply() {
        setApplying(true);
        setMessages(undefined);
        try {
            const { error } = await applyMdResults(job.jobid);
            if (error) setMessages([error]);
        } finally {
            setApplying(false);
        }
    }

    async function onShowResidues(keys) {
        const { error, missing } = await showResidues(job, local.stash, keys);
        if (error) setMessages([error]);
        else if (missing?.length) setMessages([`Not found in the loaded structure: ${missing.join(', ')}`]);
        else setMessages(undefined);
    }

    async function onLoadPose(name, copy, sdf) {
        const { error, compound } = await loadFinalPose(job, local.stash, name, copy, sdf);
        setMessages([error || `Loaded the final pose of run ${copy} as ${compound?.resSpec || 'a new compound'}.`]);
    }

    return (
        <DialogContentSection title={title} titleVariant="subtitle1" collapsible defaultExpand>
            <Typography component="div" sx={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                {statusLine}
            </Typography>
            {prov.engine_version && (
                <Typography component="div" sx={{ fontSize: '0.8rem', opacity: 0.75, marginBottom: '0.5rem' }}>
                    {`${prov.engine_version}${md.method?.protein_ff ? ` · ${md.method.protein_ff} / ${md.method.ligand_ff} / ${md.method.water}` : ''}`}
                </Typography>
            )}
            {methodOptions(md.method).length > 0 && (
                <Typography component="div" sx={{ fontSize: '0.8rem', marginBottom: '0.5rem' }}>
                    {methodOptions(md.method).join(' · ')}
                </Typography>
            )}
            {(md.warnings || []).length > 0 && (
                <Box
                    component="ul"
                    sx={{
                        margin: '0 0 0.5rem 1rem', padding: 0, fontSize: '0.85rem', color: 'var(--marketing-orange, #b8650c)',
                    }}
                >
                    {md.warnings.map((w) => <li key={w}>{w}</li>)}
                </Box>
            )}
            {messages && <ImportErrors errors={messages} />}

            {/* At the top, not the bottom: the results run to thousands of pixels. */}
            <MdReportPanel md={md} title={title} method={method} />

            {compounds.map((c) => (
                <CompoundResults
                    key={c.name}
                    compound={c}
                    onShowResidues={onShowResidues}
                    onLoadPose={onLoadPose}
                />
            ))}

            <Box sx={{
                display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.75rem',
            }}
            >
                <BMapsTooltip title={applyDisabled || `Write the pose verdict, ligand RMSD and top H-bond onto ${candidates.length} workspace compound${candidates.length === 1 ? '' : 's'}`}>
                    {wrapForDisabledTooltip(
                        <DialogPrimaryButton onClick={apply} disabled={!!applyDisabled || applying} sx={{ width: '100%' }}>
                            {applying ? 'Applying…' : 'Apply results to compounds'}
                        </DialogPrimaryButton>
                    )}
                </BMapsTooltip>
                {applied.length > 0 && (
                    <span style={{ fontSize: '0.9rem' }}>
                        {`Applied to ${applied.length} compound${applied.length === 1 ? '' : 's'}`}
                        {local.missing?.length ? `; not in the workspace: ${local.missing.join(', ')}` : ''}
                    </span>
                )}
            </Box>
        </DialogContentSection>
    );
}

/**
 * The options a job ran with, from the packet's method (thermostat when not
 * the engine's Langevin default, restraints, annealing, replica exchange).
 * @param {object} [method]
 * @returns {string[]}
 */
export function methodOptions(method) {
    if (!method) return [];
    return [
        /Nose-Hoover/.test(method.thermostat || '') ? `Thermostat: ${method.thermostat}` : null,
        method.restraints ? `Restraints: ${method.restraints}` : null,
        method.annealing ? `Annealing: ${method.annealing}` : null,
        method.replica_exchange ? `Replica exchange: ${method.replica_exchange}` : null,
    ].filter(Boolean);
}

/**
 * "replica exchange acceptance 25–35%" for a compound's REMD block.
 * @param {object} [remd]
 * @returns {string|null}
 */
export function remdLine(remd) {
    const acc = remd?.acceptance || [];
    if (acc.length === 0) return null;
    const lo = Math.round(Math.min(...acc) * 100);
    const hi = Math.round(Math.max(...acc) * 100);
    return `replica exchange acceptance ${lo === hi ? `${lo}` : `${lo}–${hi}`}%`;
}

function CompoundResults({ compound, onShowResidues, onLoadPose }) {
    // The two RMSD series are drawn together by the protein-ligand RMSD chart.
    const plots = useMemo(
        () => availablePlots(compound).filter((p) => !RMSD_PLOT_KEYS.includes(p.key)),
        [compound],
    );
    const [plotKey, setPlotKey] = useState(plots[0]?.key);
    const rmsf = useMemo(() => meanRmsf(compound), [compound]);

    if (compound.status !== 'succeeded') {
        return (
            <Box sx={{ margin: '0.75rem 0' }}>
                <Typography component="div" sx={{ fontWeight: 600 }}>{compound.name}</Typography>
                <Typography component="div" sx={{ color: 'var(--marketing-orange, #b8650c)', fontSize: '0.9rem' }}>
                    {`Not simulated: ${compound.error || 'failed'}`}
                </Typography>
            </Box>
        );
    }
    const agg = compound.aggregate || {};
    const copies = compound.copies || [];
    const plot = plots.find((p) => p.key === plotKey) || plots[0];
    const hasDensity = copies.some((c) => c.density_kg_m3);
    const contacts = agg.contacts || [];
    const topRmsf = [...rmsf].sort((a, b) => b.rmsf_a - a.rmsf_a).slice(0, RMSF_ROWS);
    const sys = compound.system || {};

    return (
        <DialogContentSection
            title={compound.name}
            titleUpperCase={false}
            titleIcon={<PoseBadge pose={agg.pose} />}
            titleVariant="subtitle2"
            collapsible
            defaultExpand
        >
            <Typography component="div" sx={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                {[
                    `Ligand RMSD ${fmtPm(agg.ligand_rmsd_a)} Å`,
                    `backbone ${fmtPm(agg.backbone_rmsd_a)} Å`,
                    `T ${fmtPm(agg.temperature_k, 1)} K`,
                    agg.equilibrated === false ? 'not all runs equilibrated' : null,
                    remdLine(compound.replica_exchange),
                    sys.atoms ? `${sys.atoms.toLocaleString()} atoms` : null,
                    sys.box_a ? `box ${fmt(sys.box_a[0], 1)} Å` : null,
                ].filter(Boolean).join(' · ')}
            </Typography>

            <TableContainer sx={{ marginBottom: '0.5rem' }}>
                <Table size="small" aria-label={`Independent runs of ${compound.name}`}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Run</TableCell>
                            <TableCell>Pose</TableCell>
                            <TableCell align="right">Ligand RMSD mean / max / final (Å)</TableCell>
                            <TableCell align="right">Backbone (Å)</TableCell>
                            <TableCell align="right">T (K)</TableCell>
                            {hasDensity && <TableCell align="right">Density (kg/m³)</TableCell>}
                            <TableCell>Final pose</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {copies.map((c) => (
                            <TableRow key={c.copy}>
                                <TableCell>{c.copy}</TableCell>
                                <TableCell><PoseBadge pose={c.pose} /></TableCell>
                                <TableCell align="right">
                                    {`${fmt(c.ligand_rmsd_a?.mean)} / ${fmt(c.ligand_rmsd_a?.max)} / ${fmt(c.ligand_rmsd_a?.final)}`}
                                </TableCell>
                                <TableCell align="right">{fmt(c.backbone_rmsd_a?.mean)}</TableCell>
                                <TableCell align="right">
                                    {fmtPm(c.temperature_k, 1)}
                                    {c.equilibrated === false ? ' *' : ''}
                                </TableCell>
                                {hasDensity && <TableCell align="right">{fmt(c.density_kg_m3?.mean, 0)}</TableCell>}
                                <TableCell>
                                    {c.final_pose?.ligand_sdf ? (
                                        <Link /* eslint-disable-line jsx-a11y/anchor-is-valid */
                                            component="button"
                                            onClick={() => onLoadPose(
                                                compound.name, c.copy, c.final_pose.ligand_sdf,
                                            )}
                                            title="Load this run's last frame of the ligand as a new compound"
                                            sx={{ color: 'var(--marketing-blue)' }}
                                        >
                                            Load
                                        </Link>
                                    ) : ''}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {copies.some((c) => c.equilibrated === false) && (
                <FormNote>
                    * the potential energy was still drifting over the analysed window.
                </FormNote>
            )}

            <ProteinLigandRmsdChart compound={compound} />
            <LigandRmsfChart compound={compound} />
            <LigandInteractionDiagram compound={compound} />
            <ContactsHistogramChart compound={compound} />
            <ContactsTimelineChart compound={compound} />
            <LigandPropertiesChart compound={compound} />
            <LigandTorsionsPanel compound={compound} />
            <SecondaryStructureChart compound={compound} />

            {plot && (
                <Box sx={{ marginTop: '0.5rem' }}>
                    <Select
                        id={`md-plot-${compound.name}`}
                        label="Plot"
                        value={plot.key}
                        onChange={(e) => setPlotKey(e.target.value)}
                        sx={{ width: '100%', marginBottom: '0.25rem' }}
                    >
                        {plots.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                    </Select>
                    <MdLineChart
                        series={seriesForPlot(compound, plot.key)}
                        xLabel="time (ps)"
                        yLabel={plot.label}
                        ariaLabel={`${plot.label} of ${compound.name}`}
                    />
                </Box>
            )}

            <Typography component="div" sx={{ fontWeight: 600, marginTop: '0.75rem' }}>
                {`Protein–ligand hydrogen bonds (${(agg.hbonds || []).length})`}
            </Typography>
            {(agg.hbonds || []).length > 0 ? (
                <TableContainer sx={{ marginBottom: '0.5rem' }}>
                    <Table size="small" aria-label={`Hydrogen bonds of ${compound.name}`}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Residue</TableCell>
                                <TableCell>Protein atom</TableCell>
                                <TableCell>Ligand atom</TableCell>
                                <TableCell>Ligand is</TableCell>
                                <TableCell align="right">Occupancy</TableCell>
                                <TableCell align="right">Runs</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {agg.hbonds.map((h) => (
                                <TableRow key={`${h.residue}-${h.protein_atom}-${h.ligand_atom}-${h.ligand_role}`}>
                                    <TableCell>
                                        <ResidueLink
                                            residue={h.residue}
                                            resname={h.resname}
                                            onShow={onShowResidues}
                                        />
                                    </TableCell>
                                    <TableCell>{h.protein_atom}</TableCell>
                                    <TableCell>{h.ligand_atom}</TableCell>
                                    <TableCell>{h.ligand_role}</TableCell>
                                    <TableCell align="right">{`${pct(h.occupancy)} ± ${pct(h.occupancy_sd)}`}</TableCell>
                                    <TableCell align="right">{`${h.copies_seen}/${copies.length}`}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : <FormNote>None above the detection threshold.</FormNote>}

            <Box sx={{
                display: 'flex', alignItems: 'baseline', gap: '1rem', marginTop: '0.75rem',
            }}
            >
                <Typography component="div" sx={{ fontWeight: 600 }}>
                    {`Contacts within 4 Å (${contacts.length})`}
                </Typography>
                {contacts.some((c) => c.occupancy >= CONTACT_SELECT_MIN) && (
                    <Link /* eslint-disable-line jsx-a11y/anchor-is-valid */
                        component="button"
                        onClick={() => onShowResidues(
                            contacts
                                .filter((c) => c.occupancy >= CONTACT_SELECT_MIN)
                                .map((c) => c.residue),
                        )}
                        sx={{ color: 'var(--marketing-blue)', fontSize: '0.85rem' }}
                    >
                        {`Select the residues in contact ≥ ${pct(CONTACT_SELECT_MIN)} of the time`}
                    </Link>
                )}
            </Box>
            <Box sx={{
                display: 'flex', flexWrap: 'wrap', gap: '0.25rem 1rem', fontSize: '0.85rem', margin: '0.25rem 0 0.5rem',
            }}
            >
                {contacts.slice(0, CONTACT_ROWS).map((c) => (
                    <span key={c.residue}>
                        <ResidueLink
                            residue={c.residue}
                            resname={c.resname}
                            onShow={onShowResidues}
                        />
                        {` ${pct(c.occupancy)}`}
                    </span>
                ))}
                {contacts.length > CONTACT_ROWS && <span>{`and ${contacts.length - CONTACT_ROWS} more`}</span>}
            </Box>

            {rmsf.length > 0 && (
                <>
                    <Typography component="div" sx={{ fontWeight: 600, marginTop: '0.75rem' }}>
                        Flexibility (CA RMSF, mean over the independent runs)
                    </Typography>
                    <MdLineChart
                        series={[{ name: 'RMSF', points: rmsf.map((r, i) => [i + 1, r.rmsf_a]) }]}
                        xLabel="residue (sequence order)"
                        yLabel="RMSF (Å)"
                        height={160}
                        ariaLabel={`CA RMSF of ${compound.name}`}
                    />
                    <Box sx={{
                        display: 'flex', flexWrap: 'wrap', gap: '0.25rem 1rem', fontSize: '0.85rem', margin: '0.25rem 0',
                    }}
                    >
                        <span>Most flexible:</span>
                        {topRmsf.map((r) => (
                            <span key={r.residue}>
                                <ResidueLink
                                    residue={r.residue}
                                    resname={r.resname}
                                    onShow={onShowResidues}
                                />
                                {` ${fmt(r.rmsf_a)} Å`}
                            </span>
                        ))}
                    </Box>
                </>
            )}
        </DialogContentSection>
    );
}

function FormNote({ children }) {
    return <div style={{ fontSize: '0.8rem', fontStyle: 'italic', margin: '0.1rem 0 0.5rem' }}>{children}</div>;
}
